"use client";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { KbdShortcut } from "@/components/ui/kbd-shortcut";
import type { RunPhase } from "@/stores/search-runs-store";
import { cn } from "@/lib/utils";

/**
 * Terminal phases that left the search without fresh results, so re-running
 * the unchanged saved definition is allowed (`failed` prop below). Callers
 * feed the store's phase through this rather than comparing to `"failed"`.
 */
export function isRerunnablePhase(phase: RunPhase | undefined): boolean {
  return phase === "failed" || phase === "cancelled";
}

export interface RunSearchButtonProps {
  /** Draft differs from the saved definition. */
  dirty: boolean;
  /** Draft carries no search intent; running it would be pointless. */
  isEmpty: boolean;
  /**
   * Latest run stopped without results (failed or cancelled), so re-running
   * the same definition is allowed. See `isRerunnablePhase`.
   */
  failed: boolean;
  /** A run request is in flight. */
  pending: boolean;
  onRun: () => void;
  /**
   * Toolbar placement: skip the reserved width the panel footer uses to hold
   * the button still while the spinner comes and goes.
   */
  compact?: boolean;
  className?: string;
}

/** The button's own enabled gate; `useRunSearchShortcut` mirrors it. */
export function canRunSearch({
  dirty,
  isEmpty,
  failed,
  pending,
}: Pick<RunSearchButtonProps, "dirty" | "isEmpty" | "failed" | "pending">) {
  return !pending && !isEmpty && (dirty || failed);
}

/**
 * Save is Run: the only way a draft becomes a run. Enabled when there is
 * something new to run (dirty) or the last run failed, never while a run is
 * pending or the draft is empty. The Cmd/Ctrl+Enter hint is advertised here;
 * the listener itself lives in `useRunSearchShortcut`, mounted once per view
 * so it survives a collapsed filter panel and a second copy of this button.
 */
export function RunSearchButton({
  dirty,
  isEmpty,
  failed,
  pending,
  onRun,
  compact = false,
  className,
}: RunSearchButtonProps) {
  const enabled = canRunSearch({ dirty, isEmpty, failed, pending });

  return (
    <Button
      type="button"
      variant="primary"
      size="sm"
      aria-label="Run search"
      aria-keyshortcuts="Meta+Enter Control+Enter"
      aria-busy={pending || undefined}
      disabled={!enabled}
      onClick={onRun}
      icon={
        pending ? (
          <Icon name="IconLoadingCircle" size={16} className="animate-spin" />
        ) : undefined
      }
      // Wide enough for the pending state (spinner + label + shortcut) so the
      // button does not resize when the spinner comes and goes.
      className={cn(!compact && "min-w-44", className)}
    >
      Run search
      <KbdShortcut keys={["mod", "↵"]} variant="primary" />
    </Button>
  );
}
